// SVG icon markup for map markers. Kept as pure string builders (no DOM)
// so the marker math is unit-testable; the strings are passed to
// L.divIcon(). Colors are passed in so the theme can drive them.

// Color themes for multi-segment donut markers: one entry per part, in
// order. "default" is the legacy palette and stays the first option.
export const PALETTES = {
  default: ['rgb(76, 175, 80)', 'rgb(33, 150, 243)', 'rgb(255, 152, 0)', 'rgb(156, 39, 176)'],
  warm: ['#b71c1c', '#e65100', '#f9a825', '#8d6e63'],
  cool: ['#0d47a1', '#00838f', '#4db6ac', '#7e57c2'],
  mono: ['#37474f', '#607d8b', '#90a4ae', '#cfd8dc'],
  okabe: ['#0072b2', '#e69f00', '#009e73', '#cc79a7'],
  viridis: ['#440154', '#3b528b', '#21918c', '#fde725']
};

const PIE_COLORS = PALETTES.default;

// Map base layers the user can switch between; keys are the values stored
// in the visualization config (config.baseLayer).
export const TILE_LAYERS = {
  osm: {
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap'
  },
  light: {
    url: 'https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png',
    attribution: '&copy; CARTO &copy; OpenStreetMap'
  },
  dark: {
    url: 'https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}.png',
    attribution: '&copy; CARTO &copy; OpenStreetMap'
  },
  topo: {
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenTopoMap'
  },
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{x}/{y}',
    attribution: '&copy; Esri'
  }
};

export function pieColor(index) {
  return PIE_COLORS[index % PIE_COLORS.length];
}

// fraction in [0, 1] rendered as a donut slice of the circle. `scale`
// multiplies the SVG size (default 1 = the original 40x40 markup).
export function pieIconHtml(fraction, stroke, track, scale = 1) {
  const clamped = Math.min(1, Math.max(0, fraction || 0));
  const radius = 16;
  const circumference = 2 * Math.PI * radius;
  const dash = clamped * circumference;
  const size = 40 * scale;
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 40 40">` +
    `<circle cx="20" cy="20" r="${radius}" fill="none" stroke="${
      track || 'rgba(0,0,0,0.15)'
    }" stroke-width="8"/>` +
    `<circle cx="20" cy="20" r="${radius}" fill="none" stroke="${
      stroke || pieColor(0)
    }" stroke-width="8" ` +
    `stroke-dasharray="${dash.toFixed(2)} ${circumference.toFixed(
      2
    )}" transform="rotate(-90 20 20)"/>` +
    `</svg>`
  );
}

// parts [{ label, value }] rendered as a multi-segment donut: one ring
// segment per part with a positive value, in order, colored by the palette
// (default: PALETTES.default). `scale` multiplies the SVG size (default 1).
export function donutIconHtml(parts, palette = PALETTES.default, scale = 1) {
  const colors = palette;
  const radius = 8;
  const circumference = 2 * Math.PI * radius;
  const visible = (parts || [])
    .map((p, i) => ({ value: Number(p.value) || 0, color: colors[i % colors.length] }))
    .filter((p) => p.value > 0);
  const total = visible.reduce((sum, p) => sum + p.value, 0);
  let offset = 0;
  const segments = visible
    .map((p) => {
      const dash = (p.value / total) * circumference;
      const html =
        `<circle cx="20" cy="20" r="${radius}" fill="none" stroke="${p.color}" stroke-width="12" ` +
        `stroke-dasharray="${dash.toFixed(2)} ${(circumference - dash).toFixed(
          2
        )}" stroke-dashoffset="${(-offset).toFixed(2)}" ` +
        `transform="rotate(-90 20 20)"/>`;
      offset += dash;
      return html;
    })
    .join('');
  const size = 40 * scale;
  return (
    `<svg width="${size}" height="${size}" viewBox="0 0 40 40">` +
    `<circle cx="20" cy="20" r="${radius}" fill="none" stroke="rgba(0,0,0,0.15)" stroke-width="12"/>` +
    segments +
    `</svg>`
  );
}

// A solid dot of the given radius, for the "dots" layer. `size` is the
// radius in px, so the marker box is 2*size square, anchored at its center.
export function dotIconHtml(size, color, stroke = '#ffffff') {
  const r = Number(size) || 4;
  return (
    `<div style="width:${r * 2}px;height:${r * 2}px;border-radius:50%;` +
    `background:${color || '#2e7d32'};border:2px solid ${stroke};opacity:0.85"></div>`
  );
}

// A text label, centered horizontally and sitting just below the marker
// anchor. Used by the "labels" layer; `color` is the text color.
export function labelIconHtml(text, color) {
  const safe = String(text == null ? '' : text);
  return (
    `<div style="font: 500 11px/1 system-ui,sans-serif;` +
    `color:${color || '#1c1b1f'};white-space:nowrap;transform:translate(-50%,-2px);` +
    `text-shadow:0 0 2px #fff,0 0 2px #fff,0 0 2px #fff">${safe}</div>`
  );
}

// fraction in [0, 1] rendered as a vertical bar, 0 -> 4px, 1 -> 32px.
// `scale` multiplies the SVG size (default 1 = the original 16x36 markup).
export function barIconHtml(fraction, color, scale = 1) {
  const clamped = Math.min(1, Math.max(0, fraction || 0));
  const height = Math.max(4, clamped * 32);
  const w = 16 * scale;
  const h = 36 * scale;
  return (
    `<svg width="${w}" height="${h}" viewBox="0 0 16 36">` +
    `<rect x="2" y="${(36 - height).toFixed(2)}" width="12" height="${height.toFixed(2)}" ` +
    `fill="${color || pieColor(0)}" rx="1"/>` +
    `</svg>`
  );
}
